import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box } from '@src/atoms/Box';
import { Button } from '@src/atoms/Button';
import { Select } from '@src/atoms/Select';
import { Text } from '@src/atoms/Text';
import { useUnit } from 'effector-react';
import { $tableStore } from '../store';
import { changePagingMoveEvent } from '../store/events/changePagingMoveEvent';
import { changePagingLimitEvent } from '../store/events/changePagingLimitEvent';
import { DATA_ELEM_PAGING } from '../consts';

import styles from './DataTablePaging.scss';
import { IDataTableProps } from '../models';
import { getPaginationPagesList } from '@src/molecules/DataTable/_Paging/utils';
import { DataTablePageList } from './_PageList/DataTablePageList';
import { changePageEvent } from '@src/molecules/DataTable/store/events/changePageEvent';
import { BreakpointsContext } from '@src/providers/Breakpoints';

interface IDataTablePagingProps extends Pick<IDataTableProps, 'pagingProps'> {
  currentPageRows?: number;
  totalRows?: number;
  hidePaging: boolean;
}

export const DataTablePaging: FC<IDataTablePagingProps> = ({ pagingProps, totalRows, currentPageRows, hidePaging }) => {
  const { isMobile } = useContext(BreakpointsContext);
  const [{ paging, pagingOptions }, changePagingMoveEventFn, changePagingLimitEventFn, changePageEventFn] = useUnit([
    $tableStore,
    changePagingMoveEvent,
    changePagingLimitEvent,
    changePageEvent,
  ]);
  const [pagingShowTo, setPagingShowTo] = useState<number | undefined>();

  const handleClick = useCallback(
    (direction: 'left' | 'right') => {
      changePagingMoveEventFn({
        totalRows,
        direction,
      });
    },
    [totalRows, changePagingMoveEventFn],
  );

  const handleChangeSelect = useCallback(
    (data) => {
      const limit = Number(data.value);
      if (limit > 0) {
        changePagingLimitEventFn({
          limit: limit,
        });
      }
    },
    [changePagingLimitEventFn],
  );

  const handleLeftClick = useCallback(() => handleClick('left'), [handleClick]);
  const handleRightClick = useCallback(() => handleClick('right'), [handleClick]);

  useEffect(() => {
    let showTo: number | undefined;
    if (totalRows !== undefined && currentPageRows !== undefined && paging.limit) {
      showTo = (paging.offset || 0) + (paging.limit || 0);
      if (showTo > totalRows) {
        showTo = totalRows;
      }
    }
    setPagingShowTo(showTo);
  }, [currentPageRows, paging.limit, paging.offset, totalRows]);

  const pagingLimitValue = useMemo(() => {
    for (const opt of pagingOptions) {
      if (paging?.limit && Number(opt.value) === paging.limit) {
        return opt;
      }
    }
    return pagingOptions[0];
  }, [paging.limit, pagingOptions]);

  const isDisabledNextBtn = (paging.offset || 0) + (paging.limit || 0) >= (totalRows || 0);
  const isDisabledPreBtn = paging?.offset === 0;

  const currentPage = (paging.offset || 0) / pagingLimitValue.value + 1;
  const pagesList = useMemo(
    () => getPaginationPagesList({ total: totalRows, currentPage, perPage: pagingLimitValue.value }),
    [currentPage, pagingLimitValue, totalRows],
  );
  const onPageChange = useCallback(
    (newPage: number) => {
      changePageEventFn({ newPage, totalRows });
    },
    [changePageEventFn, totalRows],
  );

  if (hidePaging) return null;

  return (
    <>
      <Box
        fullWidth
        alignItems="center"
        gap={{ DESKTOP: 8, MOBILE: 16 }}
        data-elem={DATA_ELEM_PAGING}
        justifyContent={pagingProps?.justifyContent || 'spaceBetween'}
        wrap={isMobile}
        marginTop={16}
        paddingLeft={2}
        paddingRight={2}
        paddingBottom={2}
      >
        <Box gap={4} alignItems="center" fullWidth={isMobile} justifyContent={{ MOBILE: 'center' }}>
          <Button
            size={pagingProps?.size || 'sm'}
            id={'moveLeftBtn'}
            view={'textLight'}
            onClick={handleLeftClick}
            disabled={isDisabledPreBtn}
            preIcon="outlineLeft"
          />
          <DataTablePageList list={pagesList} onPageChange={onPageChange} currentActivePage={currentPage} />
          <Button
            size={pagingProps?.size || 'sm'}
            id={'moveRightBtn'}
            view={'textLight'}
            onClick={handleRightClick}
            disabled={isDisabledNextBtn}
            preIcon="outlineRight"
          />
        </Box>
        <Box alignItems="center" gap={16} fullWidth={isMobile} className={isMobile ? styles.limitWrapperMobile : ''}>
          {pagingProps?.children}

          {pagingShowTo !== undefined && pagingShowTo > 0 && (
            <Box
              alignItems="center"
              justifyContent={{ DESKTOP: 'center', MOBILE: 'spaceBetween' }}
              gap={16}
              fullWidth={isMobile}
            >
              <Text
                id="pagingShows"
                nowrap
                {...(pagingProps?.textProps || { size: 'secondary', color: 'basicGreySemiDark' })}
              >
                Показано {paging.offset}-{pagingShowTo} из {totalRows}
              </Text>
              <Select
                className={styles.selectContainer}
                id="DataTable_PagingLimit"
                onChange={handleChangeSelect}
                isSearchable={false}
                value={pagingLimitValue}
                options={pagingOptions}
                size={pagingProps?.size || 'sm'}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
